import { Box, Stack, Typography } from '@mui/material';
import { createPortal } from 'react-dom';

import type { WallBoard, WallCard } from '@walls/store';
import { CardOwnerLabel } from '../cards/CardOwnerLabel';
import { ImageCardImage } from '../cards/ImageCardImage';
import { useCardImage } from '../cards/hooks/useCardImage';
import { useCardPermissions } from '../cards/hooks/useCardPermissions';
import { useParticipantName } from '../cards/hooks/useParticipantName';
import { getCardTitleHtml, getCardTitleText, isImageCard } from '../cards/utils/cardDisplay';
import { MultilineText } from '../cards/utils/MultilineText';
import { getClusterChildren, isClusterCard, sortByZPosition } from '../selectors/boardCardSelectors';
import { getCardText } from '../utils/cardDisplay';
import { CollapsedClusterFooter } from './CollapsedClusterFooter';
import type { ClusterChildSortDrag } from './types';

export function ClusterChildDragHelper({
  board,
  child,
  drag,
  zoom,
}: {
  board: WallBoard;
  child: WallCard;
  drag: ClusterChildSortDrag;
  zoom: number;
}) {
  const helper = (
    <Box
      sx={{
        bgcolor: child.color || '#ffff99',
        boxShadow: '0 6px 18px rgba(16, 24, 40, 0.28)',
        color: '#111',
        height: drag.boardSize.height,
        left: drag.pointerX - drag.grabOffsetX,
        minHeight: drag.boardSize.height,
        opacity: 0.9,
        overflow: 'hidden',
        pointerEvents: 'none',
        position: 'fixed',
        top: drag.pointerY - drag.grabOffsetY,
        transform: `scale(${zoom})`,
        transformOrigin: 'top left',
        width: drag.boardSize.width,
        wordBreak: 'break-word',
        zIndex: 1800,
      }}
    >
      {isClusterCard(child) ? (
        <ClusterDragPreview board={board} cluster={child} />
      ) : (
        <CardDragPreview board={board} card={child} />
      )}
    </Box>
  );

  return createPortal(helper, document.body);
}

function CardDragPreview({ board, card }: { board: WallBoard; card: WallCard }) {
  const ownerName = useCardDragPreviewOwnerName(board, card);

  if (isImageCard(card)) {
    return <ImageCardDragPreview board={board} card={card} ownerName={ownerName} />;
  }

  return (
    <Box
      sx={{
        bgcolor: card.color || '#ffff99',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'inherit',
        width: '100%',
      }}
    >
      <Box sx={{ flex: 1, p: 1.25 }}>
        <Typography component="span" variant="body2" sx={{ color: 'inherit' }}>
          <MultilineText text={getCardText(card)} />
        </Typography>
      </Box>
      <CardOwnerLabel ownerName={ownerName} />
    </Box>
  );
}

function ImageCardDragPreview({
  board,
  card,
  ownerName,
}: {
  board: WallBoard;
  card: WallCard;
  ownerName: string;
}) {
  const imageUrl = useCardImage(board, card);
  const cardTitleText = getCardTitleText(card);
  const cardTitleHtml = getCardTitleHtml(card);

  return (
    <Box
      sx={{
        bgcolor: card.color || '#ffff99',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'inherit',
        width: '100%',
      }}
    >
      {imageUrl && <ImageCardImage alt={cardTitleText || 'Card image'} src={imageUrl} />}
      {cardTitleHtml ? (
        <Typography
          variant="body2"
          sx={dragPreviewTitleStyles}
          dangerouslySetInnerHTML={{ __html: cardTitleHtml }}
        />
      ) : (
        cardTitleText && (
          <Typography variant="body2" sx={dragPreviewTitleStyles}>
            <MultilineText text={cardTitleText} />
          </Typography>
        )
      )}
      <CardOwnerLabel ownerName={ownerName} />
    </Box>
  );
}

const dragPreviewTitleStyles = {
  color: 'inherit',
  flex: '0 0 auto',
  p: 1,
  wordBreak: 'break-word',
};

function useCardDragPreviewOwnerName(board: WallBoard, card: WallCard) {
  const permissions = useCardPermissions(board, card);
  const participantName = useParticipantName(card.ownerId);

  return permissions.showOwnerName ? participantName : '';
}

function ClusterDragPreview({ board, cluster }: { board: WallBoard; cluster: WallCard }) {
  const { rootCards, rootClusters } = getClusterChildren(cluster);
  const orderedChildren = sortByZPosition([...rootClusters, ...rootCards]);
  const childCount = orderedChildren.length;
  const isResizedCluster = cluster.width != null || cluster.height != null;

  return (
    <Box
      sx={{
        bgcolor: cluster.collapsed ? cluster.color || '#ffff99' : 'rgba(255, 255, 255, 0.5)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <Typography
        className="title"
        variant="body2"
        sx={{
          bgcolor: cluster.color || '#ffff99',
          minHeight: 38,
          p: 1.25,
          wordBreak: 'break-word',
        }}
      >
        <MultilineText text={getCardText(cluster) || 'Untitled cluster'} />
      </Typography>
      {cluster.collapsed ? (
        <CollapsedClusterFooter childCount={childCount} isLocked={cluster.isLocked} />
      ) : (
        <Stack
          spacing={0.75}
          sx={{
            alignItems: 'flex-start',
            flex: isResizedCluster ? '1 1 auto' : undefined,
            maxHeight: isResizedCluster ? undefined : 240,
            minHeight: 0,
            overflow: 'hidden',
            p: 0.75,
          }}
        >
          {orderedChildren.length === 0 ? (
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.5)',
                color: 'text.secondary',
                fontSize: 13,
                p: 1,
                width: '100%',
              }}
            >
              Empty cluster
            </Box>
          ) : (
            orderedChildren.map((childCard) => (
              <ClusterChildPreview key={childCard.id} card={childCard} />
            ))
          )}
        </Stack>
      )}
    </Box>
  );
}

function ClusterChildPreview({ card }: { card: WallCard }) {
  if (isClusterCard(card)) {
    return (
      <Box
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.5)',
          boxShadow: '0 1px 1px rgba(0,0,0,0.15)',
          minWidth: 180,
          overflow: 'hidden',
          width: card.width ?? 180,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            bgcolor: card.color || '#ffff99',
            minHeight: 32,
            p: 1,
            wordBreak: 'break-word',
          }}
        >
          <MultilineText text={getCardText(card) || 'Untitled cluster'} />
        </Typography>
        <Box
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            borderTop: '1px solid #f3f5f8',
            fontSize: 12,
            px: 0.75,
            py: 0.5,
          }}
        >
          {card.cards?.length ?? 0}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: card.color || '#ffff99',
        boxShadow: '0 1px 1px rgba(0,0,0,0.15)',
        minHeight: 38,
        p: 1,
        width: card.width ?? 180,
        wordBreak: 'break-word',
      }}
    >
      <Typography component="span" variant="body2" sx={{ color: 'inherit' }}>
        <MultilineText text={getCardText(card)} />
      </Typography>
    </Box>
  );
}
